import * as Sentry from '@sentry/nextjs';
import StorageService from '@/lib/storage/storage';
import { authConfig } from './config';
import { generateState } from './crypto';
import {
  buildLoginUrl,
  getAllowedRedirectUris,
  isAllowedSyncRedirectUri,
} from './sso';
import type { LoginRedirectInput } from './types/sso';

export async function initiateLoginRedirect(
  input: LoginRedirectInput = {},
): Promise<string> {
  const redirectUri = authConfig.redirectUri;
  const allowlist = getAllowedRedirectUris();
  if (!isAllowedSyncRedirectUri(redirectUri, allowlist)) {
    throw new Error(`Invalid redirect URI: ${redirectUri}`);
  }

  const accessToken = StorageService.get<string>(
    StorageService.Keys.ONBOARDING_AUTH_ACCESS_TOKEN,
  );

  if (!accessToken) {
    throw new Error(
      'Access token not found. User must be authenticated before redirect to Sync.',
    );
  }

  // Persist state so the callback can validate it (CSRF). Two copies, because
  // the two halves of the callback read different stores: `/auth/callback` is a
  // server route handler and can only see the cookie, while the client half and
  // React Native read session storage.
  //
  // Deliberately after every throw above: a state persisted for a redirect that
  // never happens is a live credential the callback would still honour, so it
  // is only recorded once the redirect is certain.
  const state = generateState();
  StorageService.set(StorageService.Keys.ONBOARDING_AUTH_STATE, state);

  Sentry.addBreadcrumb({
    category: 'sync initiate auth flow',
    message: 'sync build login authenticated url succeeded',
    level: 'info',
    data: {
      redirectUri,
      email: input.email,
      taxIdentifier: input.taxIdentifier,
    },
  });

  const loginUrl = buildLoginUrl({
    redirectUri,
    clientId: authConfig.clientId,
    authorization: accessToken,
    state,
    email: input.email,
    taxIdentifier: input.taxIdentifier,
  });

  return loginUrl;
}
